/** @jsxImportSource react */

import {
  Document,
  Page,
  Text,
  View,
  Font,
  Svg,
  Path,
  Defs,
  LinearGradient,
  Stop,
  Link,
} from "@react-pdf/renderer";

// ----------------------------------------------------------------------

import Kanit400 from "../../assets/fonts/Kanit/Kanit-Regular.ttf";
import Kanit500 from "../../assets/fonts/Kanit/Kanit-Medium.ttf";
//
import OpenSans400 from "../../assets/fonts/Open_Sans/OpenSans-Regular.ttf";
import OpenSans700 from "../../assets/fonts/Open_Sans/OpenSans-Bold.ttf";
import { PRIMARY_COLOR } from "./utils";

Font.register({
  family: "Kanit",
  fonts: [
    { src: Kanit400, fontWeight: 400 },
    { src: Kanit500, fontWeight: 500 },
  ],
});

Font.register({
  family: "OpenSans",
  fonts: [
    { src: OpenSans400, fontWeight: 400 },
    { src: OpenSans700, fontWeight: 700 },
  ],
});

const getFormattedDate = () => {
  const currentDate = new Date();
  const day = currentDate.getDate();
  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();

  const formattedDay = day < 10 ? `0${day}` : `${day}`;
  const formattedMonth = month < 10 ? `0${month}` : `${month}`;

  return `${formattedDay}/${formattedMonth}/${year}`;
};

function numberPoint(numero: any) {
  // eslint-disable-next-line
  var numero = numero.toFixed(2).split(".");
  numero[0] = numero[0].split(/(?=(?:...)*$)/).join(".");
  return numero.join(",");
}

const formatDate = (dateString: string): any => {
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    return dateString;
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  } else {
    return "";
  }
};

// ----------------------------------------------------------------------

export function PDFPropostaDocument({
  data,
  detalhamento,
  detalhamentoEconomia,
  mensalidade,
  urlDoLinkProsseguirAssinatura,
  NomeRepresentante,
  RazaoRepresentante,
}: {
  data: any;
  detalhamento: any;
  detalhamentoEconomia: any;
  mensalidade: any;
  urlDoLinkProsseguirAssinatura: any;
  NomeRepresentante: any;
  RazaoRepresentante: any;
}) {
  return (
    <Document title="Proposta de venda">
      <Page size="A4">
        <View
          style={{
            padding: 20,
            color: "#212121",
            flex: 1,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
              paddingHorizontal: 16,
            }}
          >
            <PDFLogo width={67.33} height={32} />

            <Text
              style={{ fontFamily: "Kanit", fontWeight: 500, fontSize: 16 }}
            >
              Proposta de venda
            </Text>

            <View style={{ width: 101 }} />
          </View>

          {/* ---------------------------------------------------------------------- */}

          <PDFOutlinedSection>
            {/* ---------------------------------------------------------------------- */}

            <PDFSectionTitle title="Dados do contratante" />

            <View style={{ flexDirection: "row" }}>
              <PDFDataField
                label={
                  data?.tipoPessoa === "PF"
                    ? "Nome do titular da conta"
                    : "Razão social"
                }
                data={
                  data?.tipoPessoa === "PF"
                    ? data?.pessoaFisica?.nomeCompleto
                    : data?.pessoaJuridica?.razaoSocial
                }
                marginBottom
                flex={2}
              />
              <PDFDataField
                label="CPF/CNPJ"
                data={
                  data?.tipoPessoa === "PF"
                    ? data?.pessoaFisica?.CPF
                    : data?.pessoaJuridica?.cnpj || data?.pessoaJuridica?.CNPJ
                }
                marginBottom
              />
              {data?.tipoPessoa === "PF" ? (
                <PDFDataField
                  label="Data de nascimento"
                  data={formatDate(data?.pessoaFisica?.dataNascimento)}
                  marginBottom
                />
              ) : (
                <View style={{ flex: 1, paddingRight: 10 }} />
              )}
            </View>

            <View style={{ flexDirection: "row" }}>
              <PDFDataField
                label="E-mail"
                data={
                  data?.tipoPessoa === "PF"
                    ? data?.pessoaFisica?.email
                    : data?.pessoaJuridica?.responsavel?.email
                }
                flex={2}
              />
              <PDFDataField
                label="WhatsApp"
                data={
                  data?.tipoPessoa === "PF"
                    ? data?.pessoaFisica?.telefone
                    : data?.pessoaJuridica?.responsavel?.telefone
                }
              />
              <PDFDataField
                label="Telefone"
                data={
                  data?.tipoPessoa === "PF"
                    ? data?.pessoaFisica?.telefone
                    : data?.pessoaJuridica?.responsavel?.telefone
                }
              />
            </View>

            <View style={{ marginBottom: 16 }} />

            {/* ---------------------------------------------------------------------- */}

            <PDFSectionTitle title="Dados do endereço" />

            <View style={{ flexDirection: "row" }}>
              <PDFDataField
                label="CEP"
                data={data?.endereco?.CEP}
                marginBottom
              />
              <PDFDataField
                label="Logradouro"
                data={data?.endereco?.logradouro}
                marginBottom
                flex={2}
              />
              <PDFDataField
                label="Número"
                data={data?.endereco?.numero}
                marginBottom
                flex={2}
              />
            </View>

            <View style={{ flexDirection: "row" }}>
              <PDFDataField
                label="Complemento"
                data={data?.endereco?.complemento}
                marginBottom
              />
              <PDFDataField
                label="Bairro"
                data={data?.endereco?.bairro}
                marginBottom
                flex={2}
              />
              <PDFDataField
                label="Cidade"
                data={data?.endereco?.cidade}
                marginBottom
                flex={2}
              />
            </View>

            <View style={{ flexDirection: "row" }}>
              <PDFDataField label="Estado" data={data?.endereco?.estado} />
            </View>

            <View style={{ marginBottom: 16 }} />

            {/* ---------------------------------------------------------------------- */}

            <PDFSectionTitle title="Dados da fatura" />

            <View style={{ flexDirection: "row" }}>
              <PDFDataField
                label="Concessionária"
                data={data?.fatura?.concessionaria}
                flex={7}
              />
              <PDFDataField
                label="Número de instalação"
                data={data?.fatura?.numeroInstalacao}
                flex={5}
              />
              <PDFDataField
                label="Dia de vencimento da fatura"
                data={data?.fatura?.dataVencimentoFatura}
                flex={7}
              />
              <PDFDataField
                label="Forma de pagamento"
                data={data?.dadosBancario?.formaPagamento}
                flex={5}
              />
            </View>
          </PDFOutlinedSection>

          {/* ---------------------------------------------------------------------- */}

          <View style={{ paddingVertical: 10, paddingHorizontal: 20 }}>
            <PDFSectionTitle title="Detalhamento da economia" />

            <PDFOutlinedSection dense>
              <View style={{ alignItems: "center", justifyContent: "center" }}>
                <PDFSectionTitle title="Com a AXS você pode economizar até:" />
                <Text
                  style={{
                    fontFamily: "OpenSans",
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                >
                  R$ {detalhamentoEconomia}*
                </Text>
              </View>
            </PDFOutlinedSection>

            <View style={{ flexDirection: "row", gap: 16, marginTop: 8 }}>
              <View style={{ flex: 1 }}>
                <PDFOutlinedSection dense>
                  <PDFDataField
                    label="Mensalidade AXS"
                    data={mensalidade}
                    noFlex
                  />
                </PDFOutlinedSection>
              </View>
              <View style={{ flex: 1 }}>
                <PDFOutlinedSection dense>
                  <PDFDataField
                    label="Geração anual prevista"
                    data={
                      numberPoint(Number(detalhamento?.geracaoAnual)) + " kWh"
                    }
                    noFlex
                  />
                </PDFOutlinedSection>
              </View>
              <View style={{ flex: 1 }}>
                <PDFOutlinedSection dense>
                  <PDFDataField
                    label="Consumo médio previsto"
                    data={
                      numberPoint(Number(detalhamento?.consumoMedio)) + " kWh"
                    }
                    noFlex
                  />
                </PDFOutlinedSection>
              </View>
            </View>

            <View style={{ flexDirection: "row", gap: 16, marginTop: 8 }}>
              <View style={{ flex: 1 }}>
                <PDFOutlinedSection dense>
                  <PDFDataField
                    label="Emissão de CO2 evitada"
                    data={
                      numberPoint(Number(detalhamento?.carbonoEvitado)) + " kg"
                    }
                    noFlex
                  />
                </PDFOutlinedSection>
              </View>
              <View style={{ flex: 1 }}>
                <PDFOutlinedSection dense>
                  <PDFDataField
                    label="Árvores poupadas"
                    data={Number(detalhamento?.arvoresCultivadas).toFixed(0)}
                    noFlex
                  />
                </PDFOutlinedSection>
              </View>
              <View style={{ flex: 1 }}>
                <PDFOutlinedSection dense>
                  <PDFDataField
                    label="KMs em carro elétrico"
                    data={
                      numberPoint(Number(detalhamento?.kmCarroEletrico)) + " km"
                    }
                    noFlex
                  />
                </PDFOutlinedSection>
              </View>
            </View>

            <Link src={urlDoLinkProsseguirAssinatura}>
              <Text
                style={{
                  fontFamily: "Kanit",
                  fontWeight: 400,
                  fontSize: 10,
                  color: PRIMARY_COLOR,
                  textDecoration: "underline",
                  textAlign: "center",
                  marginVertical: 10,
                }}
              >
                Quer prosseguir com sua assinatura? Clique aqui.
              </Text>
            </Link>

            <Text
              style={{
                fontFamily: "OpenSans",
                fontWeight: 400,
                fontSize: 6,
                color: "#616161",
                marginBottom: 8,
              }}
            >
              *A simulação considera consumidores (B1) conectados em baixa
              tensão, ligação bifásica, e bandeira tarifária escassez hídrica.
            </Text>

            <Text
              style={{
                fontFamily: "OpenSans",
                fontWeight: 400,
                fontSize: 8,
              }}
            >
              Data de criação da proposta
            </Text>
            <Text
              style={{
                fontFamily: "OpenSans",
                fontWeight: 700,
                fontSize: 8,
              }}
            >
              {getFormattedDate()}
            </Text>
          </View>

          {/* ---------------------------------------------------------------------- */}

          <View style={{ flex: 1 }} />

          <PDFOutlinedSection>
            <PDFSectionTitle title="Agente de vendas" />

            <Text
              style={{
                fontFamily: "OpenSans",
                fontWeight: 700,
                fontSize: 12,
                marginBottom: 4,
              }}
            >
              {NomeRepresentante}
            </Text>
            <Text
              style={{ fontFamily: "OpenSans", fontWeight: 400, fontSize: 12 }}
            >
              {RazaoRepresentante}
            </Text>
          </PDFOutlinedSection>
        </View>
      </Page>
    </Document>
  );
}

// ----------------------------------------------------------------------

function PDFOutlinedSection({
  dense,
  children,
}: {
  children?: React.ReactNode;
  dense?: boolean;
}) {
  return (
    <View
      style={{
        border: "1px solid #F5F5F5",
        borderRadius: 16,
        padding: 16,
        paddingVertical: 10,
        ...(dense && { borderRadius: 8, padding: 10, paddingVertical: 8 }),
      }}
    >
      {children}
    </View>
  );
}

// ----------------------------------------------------------------------

function PDFSectionTitle({ title }: { title?: string }) {
  return (
    <Text
      style={{
        fontFamily: "Kanit",
        fontWeight: 400,
        fontSize: 13,
        marginBottom: 4,
      }}
    >
      {title}
    </Text>
  );
}

// ----------------------------------------------------------------------

function PDFDataField({
  label,
  data,
  flex = 1,
  noFlex,
  marginBottom,
}: {
  label: string;
  data?: string;
  flex?: number;
  noFlex?: boolean;
  marginBottom?: boolean;
}) {
  return (
    <View
      style={{
        paddingRight: 8,
        ...(!noFlex && { flex }),
        ...(marginBottom && { marginBottom: 3 }),
      }}
    >
      <Text style={{ fontFamily: "OpenSans", fontWeight: 400, fontSize: 10 }}>
        {label}
      </Text>
      <Text style={{ fontFamily: "OpenSans", fontWeight: 700, fontSize: 10 }}>
        {data}
      </Text>
    </View>
  );
}

// ----------------------------------------------------------------------

function PDFLogo({ width, height }: { width: number; height: number }) {
  return (
    <Svg width={width} height={height} viewBox="0 0 101 48" fill="none">
      <Path
        d="M76.223 26.8754L75.1992 30.4075C75.7282 30.9364 78.5368 32.1582 85.5396 32.8134C92.5424 33.4686 96.9549 30.561 98.2859 29.0253C99.3608 27.8821 101.183 24.6742 99.8727 20.9885C98.5623 17.3028 91.6482 15.0164 88.355 14.3338C87.8261 14.2144 86.0719 13.6172 83.2872 12.1839C80.5025 10.7505 80.9666 7.69621 81.5468 6.34821C82.0245 5.32442 84.0551 3.18468 88.355 2.81611C93.73 2.3554 95.4192 5.83632 95.9311 6.60416C96.3406 7.21844 96.8184 6.99662 97.0061 6.80892L98.4906 3.27682C98.4906 2.99016 97.5009 2.50897 97.0061 2.30422C95.7264 1.87763 92.0816 1.03471 87.7407 1.07566C83.3998 1.11661 80.5742 2.62842 79.7039 3.3792C78.5436 3.90817 76.223 6.07179 76.223 10.4946C76.223 14.9174 83.7309 17.6612 87.4848 18.4802C90.4197 18.9239 96.1154 20.968 95.4192 25.5956C94.723 30.2232 89.5324 31.2094 87.0241 31.1241C86.9046 31.1753 85.9081 31.1344 82.8777 30.561C79.8473 29.9877 77.7246 27.6261 77.0421 26.517C76.5506 26.3532 76.2913 26.6877 76.223 26.8754Z"
        fill="black"
      />
      <Path
        d="M35.9375 32.1991V33.786C43.145 27.6023 50.9873 23.7357 54.0075 22.5754L63.5288 33.786H72.5383L57.5396 15.8184C47.4655 22.2888 38.9406 29.4349 35.9375 32.1991Z"
        fill="black"
      />
      <Path
        d="M72.5371 1.58603V-0.000854492C65.3296 6.18289 57.4873 10.0494 54.4671 11.2097L44.9458 -0.000854492H35.9363L50.935 17.9668C61.0091 11.4964 69.534 4.35029 72.5371 1.58603Z"
        fill="url(#paint0_linear)"
      />
      <Path
        fillRule="evenodd"
        clip-rule="evenodd"
        d="M0 32.3522H3.63448L9.72608 20.3738H22.4212L28.3592 32.3522H34.1437L18.2236 2.25257C18.1042 1.97956 17.7424 1.45401 17.251 1.53591C16.7596 1.61781 16.6367 1.9113 16.6367 2.0478L0 32.3522ZM10.6957 18.3766H21.3944L16.1218 7.6267L10.6957 18.3766Z"
        fill="black"
      />
      <Path
        d="M21.3846 44.3952H18.1008V47.079H21.9154V47.8956H17.1016V40.3228H21.8634V41.1445H18.1008V43.5787H21.3846V44.3952Z"
        fill="black"
      />
      <Path
        d="M32.9261 47.8956H31.9217L28.1071 42.0599V47.8956H27.1027V40.3228H28.1071L31.9322 46.1844V40.3228H32.9261V47.8956Z"
        fill="black"
      />
      <Path
        d="M42.9273 44.3952H39.6435V47.079H43.4581V47.8956H38.6443V40.3228H43.4061V41.1445H39.6435V43.5787H42.9273V44.3952Z"
        fill="black"
      />
      <Path
        d="M51.4244 44.8321H49.6446V47.8956H48.6402V40.3228H51.1486C52.0021 40.3228 52.6578 40.5169 53.1158 40.9053C53.5772 41.2936 53.8079 41.8588 53.8079 42.6009C53.8079 43.0724 53.6796 43.4833 53.4228 43.8335C53.1696 44.1837 52.8157 44.4455 52.3612 44.6189L54.141 47.8332V47.8956H53.0689L51.4244 44.8321ZM49.6446 44.0156H51.1798C51.676 44.0156 52.0697 43.8873 52.3612 43.6307C52.6561 43.3741 52.8035 43.0308 52.8035 42.6009C52.8035 42.1328 52.663 41.7739 52.382 41.5242C52.1044 41.2746 51.702 41.148 51.1746 41.1445H49.6446V44.0156Z"
        fill="black"
      />
      <Path
        d="M64.751 46.9022C64.4943 47.2697 64.1352 47.5454 63.6738 47.7292C63.2158 47.9095 62.6815 47.9996 62.0709 47.9996C61.4533 47.9996 60.9051 47.8557 60.4264 47.5679C59.9476 47.2767 59.5764 46.864 59.3127 46.3301C59.0525 45.7961 58.9189 45.1771 58.912 44.4733V43.8127C58.912 42.6719 59.1774 41.7878 59.7082 41.1602C60.2425 40.5326 60.9919 40.2188 61.9564 40.2188C62.7474 40.2188 63.3841 40.4216 63.8663 40.8273C64.3486 41.2295 64.6435 41.8016 64.751 42.5436H63.7518C63.5645 41.5416 62.9677 41.0405 61.9616 41.0405C61.292 41.0405 60.7837 41.2763 60.4368 41.7479C60.0933 42.216 59.9198 42.8956 59.9164 43.7867V44.4056C59.9164 45.2552 60.1106 45.9313 60.4992 46.4341C60.8878 46.9334 61.4134 47.183 62.0761 47.183C62.4508 47.183 62.7786 47.1414 63.0597 47.0582C63.3407 46.975 63.5731 46.8346 63.757 46.6369V44.9362H62.0032V44.1248H64.751V46.9022Z"
        fill="black"
      />
      <Path
        d="M71.4371 47.8956H70.4379V40.3228H71.4371V47.8956Z"
        fill="black"
      />
      <Path
        d="M81.4903 45.9192H78.3158L77.6028 47.8956H76.5724L79.4659 40.3228H80.3402L83.2389 47.8956H82.2137L81.4903 45.9192ZM78.6176 45.0974H81.1936L79.903 41.5554L78.6176 45.0974Z"
        fill="black"
      />
      <Defs>
        <LinearGradient
          id="paint0_linear"
          x1="73.2733"
          y1="19.2245"
          x2="30.831"
          y2="5.49969"
        >
          <Stop offset="0" stopColor="#0FB88F" />
          <Stop offset="0.514413" stopColor="#38E0A0" />
          <Stop offset="1" stopColor="#6FF2AC" />
        </LinearGradient>
      </Defs>
    </Svg>
  );
}
